import { useEffect, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { ShareInfo } from "../../shared/types.js";
import { createShare, fetchShares, revokeShare } from "../api.js";
import { useSession } from "../data/usePage.js";
import { repoHref } from "../data/repo.js";
import { SystemPage, SystemSection } from "./SystemPage.js";

/**
 * `/_share?repo=&path=[&dir=1]` — share one page, or a whole directory, with a
 * guest by email
 * (docs/auth/Shares.md). Creating a share writes a row (the grant) and returns
 * the link; "email it" is a `mailto:` — the server sends no mail. Below: who
 * already has this page, with revoke.
 */
export function ShareView() {
  const [params, setParams] = useSearchParams();
  const session = useSession();
  const s = session.status === "ready" ? session.data : null;
  const repos = (s?.repos ?? []).filter((r) => !r.hidden);
  const repo = params.get("repo") || repos[0]?.name || "";
  const path = params.get("path") ?? "";
  const dir = params.get("dir") === "1";

  const [draftPath, setDraftPath] = useState(path);
  const [draftDir, setDraftDir] = useState(dir);
  const [email, setEmail] = useState("");
  const [days, setDays] = useState(30);
  const [shares, setShares] = useState<ShareInfo[] | null>(null);
  const [made, setMade] = useState<ShareInfo | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => setDraftPath(path), [path]);
  useEffect(() => setDraftDir(dir), [dir]);

  // A directory share may target the repo root (path ""); a page share needs a path.
  const picked = dir || path !== "";

  const reload = (): void => {
    if (!repo || !picked) {
      setShares(null);
      return;
    }
    fetchShares(repo, path, dir)
      .then(setShares)
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)));
  };
  useEffect(reload, [repo, path, dir]);

  const pick = (e: FormEvent): void => {
    e.preventDefault();
    setMade(null);
    const p = draftPath.replace(/^\/+|\/+$/g, "");
    setParams({ repo, path: p, ...(draftDir ? { dir: "1" } : {}) });
  };

  const share = (e: FormEvent): void => {
    e.preventDefault();
    setErr(null);
    createShare(repo, path, dir, email, days)
      .then((sh) => {
        setMade(sh);
        setEmail("");
        reload();
      })
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)));
  };

  const revoke = (token: string): void => {
    revokeShare(repo, token)
      .then(() => {
        if (made?.token === token) setMade(null);
        reload();
      })
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)));
  };

  if (!s?.user) {
    return (
      <SystemPage title="Share">
        <p>
          <a href={`/auth/login?redirect=${encodeURIComponent(location.pathname + location.search)}`}>
            Sign in
          </a>{" "}
          to share pages.
        </p>
      </SystemPage>
    );
  }

  const sender = s.user.name || s.user.email;
  // A share's email, prefilled; opened in a new tab (the server sends no mail).
  const emailShare = (sh: ShareInfo): void => {
    const what = sh.path.endsWith("/") ? "a folder" : "a page";
    const until = sh.expiresAt ? ` The link works until ${sh.expiresAt.slice(0, 10)}.` : "";
    const body = `${sender} shared ${what} with you:\n\n${sh.url}\n\nSign in as ${sh.email} to open it.${until}`;
    window.open(
      `mailto:${encodeURIComponent(sh.email)}?subject=${encodeURIComponent(
        `${s.brand}: ${sh.path}`,
      )}&body=${encodeURIComponent(body)}`,
      "_blank",
    );
  };

  return (
    <SystemPage
      title="Share"
      lead="Give one email read access to one page or directory. They sign in with that email and see only what's shared."
    >
      <SystemSection title="Page">
        <form onSubmit={pick} className="dw-share-form">
          <select value={repo} onChange={(e) => setParams({ repo: e.target.value, path: "" })}>
            {repos.map((r) => (
              <option key={r.name} value={r.name}>
                {r.name}
              </option>
            ))}
          </select>
          <span>/</span>
          <input
            type="text"
            value={draftPath}
            placeholder="path/to/page"
            onChange={(e) => setDraftPath(e.target.value)}
          />
          <label>
            <input
              type="checkbox"
              checked={draftDir}
              onChange={(e) => setDraftDir(e.target.checked)}
            />{" "}
            whole directory
          </label>
          <button type="submit" className="secondary">
            Pick
          </button>
        </form>
        {picked && (
          <p>
            {dir ? "Directory " : "Page "}
            <Link to={repoHref(repo, path)}>{repoHref(repo, path)}</Link>
            {dir && " and everything under it"}
          </p>
        )}
      </SystemSection>

      {picked && (
        <SystemSection title="Share with">
          <form onSubmit={share} className="dw-share-form">
            <input
              type="email"
              required
              value={email}
              placeholder="guest@example.com"
              onChange={(e) => setEmail(e.target.value)}
            />
            <label>
              expires in{" "}
              <input
                type="number"
                min={0}
                max={3650}
                value={days}
                className="dw-share-days"
                onChange={(e) => setDays(Number(e.target.value))}
              />{" "}
              days {days === 0 && "(never)"}
            </label>
            <button type="submit" className="primary">
              Create link
            </button>
          </form>
          {made && (
            <div className="dw-share-made">
              <p>
                Shared <code>{made.path}</code> with <b>{made.email}</b>:
              </p>
              <ShareLink url={made.url} />
              <p>
                <button className="primary" onClick={() => emailShare(made)}>
                  Email it
                </button>
              </p>
            </div>
          )}
        </SystemSection>
      )}

      {err && <p className="dw-error">{err}</p>}

      {picked && shares && (
        <SystemSection title="Shared with">
          {shares.length === 0 ? (
            <p className="dw-muted">Nobody yet.</p>
          ) : (
            <table className="dw-share-table">
              <thead>
                <tr>
                  <th>email</th>
                  <th>by</th>
                  <th>created</th>
                  <th>opened</th>
                  <th>expires</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {shares.map((sh) => (
                  <tr key={sh.token} className={sh.expired ? "dw-muted" : undefined}>
                    <td>
                      {sh.email}
                      {/* Reaches here through an enclosing directory share. */}
                      {sh.path.endsWith("/") && sh.path !== (path ? `${path}/` : "/") && (
                        <span className="dw-muted"> via {sh.path}</span>
                      )}
                      {!sh.expired && <ShareLink url={sh.url} />}
                    </td>
                    <td>{sh.createdBy}</td>
                    <td>{sh.createdAt.slice(0, 16)}</td>
                    <td>{sh.acceptedAt?.slice(0, 16) ?? "—"}</td>
                    <td>
                      {sh.expired ? "expired " : ""}
                      {sh.expiresAt?.slice(0, 10) ?? "never"}
                    </td>
                    <td>
                      {!sh.expired && (
                        <>
                          <button className="secondary" onClick={() => emailShare(sh)}>
                            Email
                          </button>{" "}
                        </>
                      )}
                      <button className="secondary" onClick={() => revoke(sh.token)}>
                        Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </SystemSection>
      )}
    </SystemPage>
  );
}

/** A share link as a read-only field: tap/click selects it all, ready to copy
 *  by hand (no clipboard API — it needs HTTPS, and the dev/LAN URL isn't). */
function ShareLink({ url }: { url: string }) {
  return (
    <input
      className="dw-share-link"
      type="text"
      readOnly
      value={url}
      onFocus={(e) => e.currentTarget.select()}
    />
  );
}
